import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { may, send } from "../api/auth";
import {
  console_,
  type AssignmentStateVocabulary,
  type AssignmentTransition,
  type CrewAssignment,
  type EventReadiness,
  type Requirement,
  type RosterPublicationPayload,
} from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { translateLabel, translateRefusal } from "../i18n/translate";
import { useSession } from "../session/SessionProvider";
import {
  barGeometry,
  buildBoard,
  cellCounts,
  cellName,
  cellsFor,
  facet,
  fillSentence,
  filterRows,
  groupRows,
  isFiltered,
  moveFocus,
  NO_FILTER,
  timelineExtent,
  toggleSelection,
  visibleWindow,
  type BoardCell,
  type BoardFilter,
  type BoardGrouping,
  type BoardRow,
} from "../system/board";
import { BOARD_KEYS, keyIntent, unavailableSentence, type BoardView } from "../system/board-keys";
import { accessibleName, toneFor } from "../system/crew";
import { clock, percent, span, whenIn } from "../system/format";
import { postsAtRisk, weightAtRisk } from "../system/readiness";
import { publicationStrip } from "../system/roster";
import { AddButton } from "../design/AddLink";
import { BookSomeone } from "./BookSomeone";

// ---------------------------------------------------------------------------
// THE CONTROL ROOM — V14. THE CREW PLANNING BOARD.
//
// 11-… §1: "Control Room — dark, dense, 13px — hosting switchable Grid and
// Timeline views of the same data. One data layer, two renderers." Grid is the
// default because it answers "who is missing?" first.
//
// IT REPLACES V13'S CREW LIST RATHER THAN SITTING BESIDE IT, and that was a
// decision with a real alternative. A second screen showing the same bookings
// would be a second thing to keep in step — the copy that stops agreeing, which
// is the failure mode this repository has now been bitten by often enough to
// name. Everything the list did (the ordered history, the moves the machine
// offers, the closed reason list) is here, inside the cell panel.
//
// WHAT THIS FILE DOES NOT DO, AND EACH IS A CHECK IN verify-V14.sh:
//
//   1. IT COMPUTES NO FILL FIGURE. Not one division, not one count of
//      assignments. `fillSentence` reads the readiness payload aloud, and the
//      payload comes from GET /v1/events/:id/readiness — the same endpoint V17
//      will replace. §V14.2: a board that says 3/4 while the score says
//      otherwise destroys trust in both.
//   2. IT NAMES NO STATE. The moves a place may make come from the server's
//      vocabulary; the tone comes from two booleans that travel with the row.
//   3. IT DECIDES NOTHING BY COLOUR. Every cell's accessible name carries the
//      state AND the cause — §V14.5 — and the visible cell carries the words
//      too. 11-… §6 measured the four semantic colours at 1.02:1 to 1.72:1 of
//      each other; in greyscale they are one colour.
//   4. IT HAS NO onKeyDown LOGIC. Every key resolves through `keyIntent` in
//      system/board-keys.ts, because §V14.4's harness note says the browser
//      harness reports an EMPTY e.key and cannot observe any of this. A handler
//      written inline would be a handler nothing can test.
//   5. IT DELETES NOTHING. Undo is a compensating transition — §V14.6 — so the
//      Proof Pack shows the mistake and its correction, which is what an
//      auditor expects to see.
// ---------------------------------------------------------------------------

type Loaded<T> = { state: "loading" } | { state: "error"; message: string } | { state: "ready"; data: T };

type Payload = {
  requirements: Requirement[];
  crew: CrewAssignment[];
  /** null when the readiness call did not answer. NOT an empty list. */
  readiness: EventReadiness | null;
  /** V23a. null when the publication call did not answer. NOT "never published". */
  publication: RosterPublicationPayload | null;
  vocabulary: AssignmentStateVocabulary;
};

/** What one applied change was, so it can be compensated for. */
type UndoEntry = {
  assignmentId: string;
  workerName: string;
  fromState: string;
  fromLabel: string;
  fromIsReserve: boolean;
  toState: string;
  toLabel: string;
  toIsReserve: boolean;
};

/** Row height in pixels. MUST match `--board-row` in design/board.css. */
const ROW_HEIGHT = 34;

function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** A group heading or a board row, at one uniform height, so both virtualise. */
type Line = { kind: "heading"; label: string } | { kind: "row"; row: BoardRow; index: number };

export function ControlRoom() {
  const { locale, t, refusal } = useT();
  const { apiBase, me } = useSession();
  const { id = "" } = useParams();

  const [loaded, setLoaded] = useState<Loaded<Payload>>({ state: "loading" });
  const [view, setView] = useState<BoardView>("grid");
  const [offering, setOffering] = useState(false);
  const [filter, setFilter] = useState<BoardFilter>(NO_FILTER);
  const [grouping, setGrouping] = useState<BoardGrouping>("none");
  const [focus, setFocus] = useState({ row: 0, cell: 0 });
  const [selected, setSelected] = useState<string[]>([]);
  const [openCell, setOpenCell] = useState<string | null>(null);
  const [openPost, setOpenPost] = useState<string | null>(null);
  const [history, setHistory] = useState<Record<string, AssignmentTransition[]>>({});
  const [reason, setReason] = useState<string>("");
  const [notice, setNotice] = useState<string | null>(null);
  const [undoable, setUndoable] = useState<UndoEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);

  const filterBox = useRef<HTMLInputElement>(null);
  const scroller = useRef<HTMLDivElement>(null);

  const canManage = me ? may(me, "assignment.manage") : false;

  const load = useCallback(async () => {
    const [requirementsResponse, crewResponse, readinessResponse, vocabularyResponse, rosterResponse] =
      await Promise.all([
        send<{ requirements?: Requirement[]; error?: { message?: string } }>(console_.listRequirements(apiBase, id)),
        send<{ assignments?: CrewAssignment[]; error?: { message?: string } }>(console_.listEventCrew(apiBase, id)),
        send<EventReadiness & { error?: { message?: string } }>(console_.eventReadiness(apiBase, id)),
        send<AssignmentStateVocabulary & { error?: { message?: string } }>(console_.listAssignmentStates(apiBase)),
        send<{ roster?: RosterPublicationPayload; error?: { message?: string } }>(
          console_.listRosterVersions(apiBase, id),
        ),
      ]);

    // THE CREW IS THE PRIMARY CALL. Requirements failing leaves a board of
    // detached people, which is a strange board and a true one; the crew
    // failing leaves nothing to show at all.
    if (crewResponse.status !== 200 || !crewResponse.body.assignments) {
      setLoaded({
        state: "error",
        message: refusal(crewResponse.body.error) ?? t("We could not load this event's crew."),
      });
      return;
    }

    const vocabulary: AssignmentStateVocabulary =
      vocabularyResponse.status === 200 && vocabularyResponse.body.states
        ? {
            states: vocabularyResponse.body.states,
            transitions: vocabularyResponse.body.transitions,
            reasons: vocabularyResponse.body.reasons,
          }
        : { states: [], transitions: [], reasons: [] };

    setLoaded({
      state: "ready",
      data: {
        requirements: requirementsResponse.status === 200 ? (requirementsResponse.body.requirements ?? []) : [],
        crew: crewResponse.body.assignments,
        // NULL AND NOT AN EMPTY ENVELOPE. Every downstream decision turns on
        // being able to tell "the fill is unknown" from "the fill is zero".
        readiness:
          readinessResponse.status === 200 && readinessResponse.body.requirements ? readinessResponse.body : null,
        // NULL AND NOT A DEFAULTED ENVELOPE, for the readiness line's reason: a
        // failed read that renders as "not published yet" is a confident wrong
        // answer about what a client can see.
        publication: rosterResponse.status === 200 && rosterResponse.body.roster ? rosterResponse.body.roster : null,
        vocabulary,
      },
    });
  }, [apiBase, id]);

  useEffect(() => {
    void load();
  }, [load]);

  const data = loaded.state === "ready" ? loaded.data : null;

  const rows = useMemo(
    () =>
      data === null
        ? []
        : buildBoard({
            requirements: data.requirements,
            crew: data.crew,
            readiness: data.readiness === null ? null : data.readiness.requirements,
          }),
    [data],
  );

  const shown = useMemo(() => filterRows(rows, filter), [rows, filter]);
  const groups = useMemo(() => groupRows(shown, grouping, t), [shown, grouping, t]);

  // ONE FLAT LIST FOR BOTH RENDERERS AND FOR BOTH GROUPINGS. Headings are lines
  // at the same height as rows, so virtualisation stays a single arithmetic
  // rather than a special case per mode — and a special case per mode is where
  // a virtualised list goes wrong at the one scroll position nobody tried.
  const lines = useMemo(() => {
    const out: Line[] = [];
    let index = 0;
    for (const group of groups) {
      if (grouping !== "none") out.push({ kind: "heading", label: group.label });
      for (const row of group.rows) {
        out.push({ kind: "row", row, index });
        index += 1;
      }
    }
    return out;
  }, [groups, grouping]);

  const counts = useMemo(() => cellCounts(shown), [shown]);
  const window_ = visibleWindow(lines.length, scrollTop, 520, ROW_HEIGHT);
  const extent = useMemo(() => timelineExtent(shown), [shown]);

  const focusedRow = shown[focus.row];
  const focusedCell = focusedRow ? cellsFor(focusedRow)[focus.cell] : undefined;
  const focusedAssignment = focusedCell?.kind === "assignment" ? focusedCell.assignment : null;

  const movesFrom = useCallback(
    (state: string) => (data?.vocabulary.transitions ?? []).filter((tr) => tr.from_state === state),
    [data],
  );

  /**
   * V23a. PUBLISH.
   *
   * NO NOTE BOX, AND THAT IS DELIBERATE. `app.roster_version` accepts one and the
   * console offers none: an append-only row that can never be deleted is the last
   * place a free-text field belongs, and absolute rule 5 applies hardest where the
   * text is permanent and shown to a third party. If a note is ever wanted it is a
   * closed list of reasons, the way V13's transitions already do it.
   *
   * RELOADS RATHER THAN PATCHING STATE. The event's dirty flag is maintained by a
   * database trigger, so the only honest source of "is there anything to publish"
   * is the database.
   */
  async function publish() {
    setBusy(true);
    const { status, body } = await send<{
      roster_version?: { version_no: number; covered_count: number; seat_count: number };
      error?: { message?: string };
    }>(console_.publishRoster(apiBase, id, null));
    setBusy(false);

    if (status !== 201 || !body.roster_version) {
      setNotice(refusal(body.error) ?? t("We could not publish this roster."));
      return;
    }
    const v = body.roster_version;
    setNotice(
      t("Published version {n}. The client now sees {covered} of {seats} posts covered.", {
        n: v.version_no,
        covered: v.covered_count,
        seats: v.seat_count,
      }),
    );
    await load();
  }

  async function showHistory(assignmentId: string) {
    if (history[assignmentId]) return;
    const { status, body } = await send<{ transitions?: AssignmentTransition[] }>(
      console_.listAssignmentHistory(apiBase, assignmentId),
    );
    if (status === 200 && body.transitions) {
      setHistory((prior) => ({ ...prior, [assignmentId]: body.transitions ?? [] }));
    }
  }

  /**
   * One change, applied.
   *
   * `record` decides whether it joins the undo batch. A change that is ITSELF
   * an undo does not — the compensation is in the record permanently and
   * undoing an undo is a new decision, not a reversal of one.
   */
  async function apply(
    assignment: { id: string; state: string; stateLabel: string; isReserve: boolean; workerName: string },
    toState: string | null,
    toLabel: string,
    isReserve: boolean | undefined,
    withReason: string | null,
    record: boolean,
  ): Promise<boolean> {
    const key = newIdempotencyKey();
    const { status, body } = await send<{ replayed?: boolean; error?: { message?: string } }>(
      console_.transitionAssignment(
        apiBase,
        assignment.id,
        {
          to_state: toState,
          ...(isReserve === undefined ? {} : { is_reserve: isReserve }),
          reason: withReason,
        },
        key,
      ),
    );
    if (status !== 200) {
      setNotice(refusal(body.error) ?? t("That change was not applied."));
      return false;
    }
    if (record) {
      setUndoable((prior) => [
        ...prior,
        {
          assignmentId: assignment.id,
          workerName: assignment.workerName,
          fromState: assignment.state,
          fromLabel: assignment.stateLabel,
          fromIsReserve: assignment.isReserve,
          toState: toState ?? assignment.state,
          toLabel,
          toIsReserve: isReserve === undefined ? assignment.isReserve : isReserve,
        },
      ]);
    }
    setHistory((prior) => {
      const next = { ...prior };
      delete next[assignment.id];
      return next;
    });
    return true;
  }

  async function move(
    assignment: { id: string; state: string; stateLabel: string; isReserve: boolean; workerName: string },
    toState: string | null,
    toLabel: string,
    isReserve?: boolean,
  ) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    setUndoable([]);
    try {
      if (await apply(assignment, toState, toLabel, isReserve, reason === "" ? null : reason, true)) {
        setReason("");
        await load();
      }
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  /** The same move, over the selection. §V14.6's "multi-select with bulk". */
  async function moveSelection(toState: string, toLabel: string) {
    if (busy || data === null) return;
    setBusy(true);
    setNotice(null);
    setUndoable([]);
    let applied = 0;
    let refused = 0;
    try {
      for (const assignmentId of selected) {
        const found = rows.flatMap((r) => r.assignments).find((a) => a.id === assignmentId);
        if (!found) continue;
        // EACH ONE CARRIES ITS OWN IDEMPOTENCY KEY, because each is its own
        // change. One key across forty would make thirty-nine of them a replay
        // of the first — which is what §2.2's guard would correctly refuse.
        if (
          await apply(
            {
              id: found.id,
              state: found.state,
              stateLabel: found.stateLabel,
              isReserve: found.isReserve,
              workerName: found.workerName,
            },
            toState,
            toLabel,
            undefined,
            reason === "" ? null : reason,
            true,
          )
        ) {
          applied += 1;
        } else {
          refused += 1;
        }
      }
      // THE COUNT IS REPORTED IN BOTH DIRECTIONS. A bulk action that says
      // nothing about what it refused is a bulk action a scheduler believes.
      setNotice(
        refused === 0
          ? t("{applied} of {total} changed.", { applied, total: selected.length })
          : t("{applied} changed, {refused} refused. The refusals are unchanged and can be looked at one at a time.", { applied, refused }),
      );
      setSelected([]);
      await load();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  /**
   * UNDO — §V14.6. A COMPENSATING TRANSITION, NEVER A DELETION.
   *
   * The reverse edge has to be one the machine offers; this screen holds no
   * copy of the machine and will not invent one. Where there is no way back —
   * a terminal state, or a refusal the person who made it has to unmake — the
   * control says so BY NAME, using the labels the server sent, rather than
   * disappearing: a control that vanishes is indistinguishable from a feature
   * that was never built.
   */
  const undoBlocked = useMemo(() => {
    if (data === null || undoable.length === 0) return null;
    for (const entry of undoable) {
      if (entry.toState === entry.fromState) continue; // a reserve flip: always reversible
      const back = movesFrom(entry.toState).some((edge) => edge.to_state === entry.fromState);
      if (!back)
        return t("{to} has no way back to {from}, so this cannot be undone. A correction has to be recorded as its own change.", {
          to: entry.toLabel,
          from: entry.fromLabel,
        });
    }
    return null;
  }, [data, undoable, movesFrom]);

  async function undo() {
    if (busy || undoable.length === 0 || undoBlocked !== null) return;
    setBusy(true);
    setNotice(null);
    const batch = undoable;
    try {
      let done = 0;
      for (const entry of batch) {
        const reversed = await apply(
          {
            id: entry.assignmentId,
            state: entry.toState,
            stateLabel: entry.toLabel,
            isReserve: entry.toIsReserve,
            workerName: entry.workerName,
          },
          entry.toState === entry.fromState ? null : entry.fromState,
          entry.fromLabel,
          entry.toIsReserve === entry.fromIsReserve ? undefined : entry.fromIsReserve,
          // THE REASON CODE IS `undo`, AND IT IS A ROW IN THE DATABASE'S CLOSED
          // LIST rather than a word spelled here. Its own description says why:
          // "the mistake and its correction both stay in the record".
          UNDO_REASON,
          false,
        );
        if (reversed) done += 1;
      }
      setUndoable([]);
      setNotice(t("{done} of {total} reversed. Both the change and its reversal stay in the record.", { done, total: batch.length }));
      await load();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(event: React.KeyboardEvent) {
    // EVERY DECISION IS IN board-keys.ts. This function's whole job is to hand
    // the key over and obey — see rule 4 in the header.
    const typing =
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      event.target instanceof HTMLSelectElement;
    const intent = keyIntent(event.key, {
      focus,
      focusedIsAssignment: focusedAssignment !== null,
      focusedIsReserve: focusedAssignment?.isReserve ?? false,
      typing,
    });
    if (intent === null) return;
    event.preventDefault();
    switch (intent.kind) {
      case "move":
        setFocus((prior) => moveFocus(prior, intent.direction, counts));
        return;
      case "open":
        if (focusedAssignment) {
          setOpenCell(focusedAssignment.id);
          void showHistory(focusedAssignment.id);
        }
        return;
      case "select":
        if (focusedAssignment) setSelected((prior) => toggleSelection(prior, focusedAssignment.id));
        return;
      case "promote":
        if (focusedAssignment && canManage) {
          void move(
            {
              id: focusedAssignment.id,
              state: focusedAssignment.state,
              stateLabel: focusedAssignment.stateLabel,
              isReserve: focusedAssignment.isReserve,
              workerName: focusedAssignment.workerName,
            },
            null,
            focusedAssignment.stateLabel,
            false,
          );
        }
        return;
      case "focusFilter":
        filterBox.current?.focus();
        return;
      case "view":
        setView(intent.view);
        return;
      case "clearSelection":
        if (selected.length > 0) setSelected([]);
        else setFilter(NO_FILTER);
        setOpenCell(null);
        return;
      case "unavailable":
        setNotice(unavailableSentence(intent, (text) => translateLabel(locale, text)));
        return;
    }
  }

  if (loaded.state === "loading") return <p className="muted">{t("Loading…")}</p>;

  if (loaded.state === "error") {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Control Room")}</h2>
        </div>
        <div className="form-message" role="status" aria-live="polite">
          <StatusChip tone="failed" label={t("Not loaded")} />
          <span>{loaded.message}</span>
        </div>
        <Link className="btn btn-quiet" to="/console/events">
          {t("Back to events")}
        </Link>
      </section>
    );
  }

  const payload = loaded.data;
  const roles = facet(rows, "role");
  const zones = facet(rows, "zone");

  return (
    <>
    {/* S11. OUTSIDE THE BOARD, because the board's keyboard shortcuts would read the
        typing in this form. */}
    {canManage && data !== null ? <BookSomeone eventId={id} requirements={data.requirements} onBooked={load} open={offering} onClose={() => setOffering(false)} /> : null}
    <div className="board" onKeyDown={onKeyDown}>
      <section className="panel">
        <div className="panel-head">
          <h2>{t("Control Room")}</h2>
          {canManage && data !== null && data.requirements.length > 0 ? (
            <AddButton label={t("Offer a post to someone")} expanded={offering} onClick={() => setOffering(!offering)} />
          ) : null}
        </div>

        {/* THE HEADLINE FIGURE, AND WHAT IT IS A FIGURE OF. `basis` is a
            sentence the SERVER sends, which is why it changed by itself when V17
            replaced raw coverage with the Readiness Score — a board showing a
            percentage without saying which of the two it was is the exact
            misreading this slice exists to prevent.

            V17 READS `score` RATHER THAN `overall * 100`. The two are the same
            number and the server derives one from the other; reading the one that
            is already a percentage takes the last piece of score arithmetic out of
            the renderer, which is what §V14.2 asks of both renderers. */}
        <div className="board-headline">
          {payload.readiness === null ? (
            <>
              <StatusChip tone="attention" label={t("Fill unknown")} />
              <span className="muted">
                {t("The readiness figures did not load, so this board can show who is booked and cannot say how full any post is. Nothing below has been guessed.")}
              </span>
            </>
          ) : (
            <>
              <StatusChip
                tone={payload.readiness.score === 100 ? "live" : "attention"}
                label={payload.readiness.score === null ? t("Nothing counted") : percent(Math.round(Number(payload.readiness.score) * 100))}
              />
              <span className="muted">
                {t("{n} posts counted.", { n: payload.readiness.counted_requirements })} {translateLabel(locale, payload.readiness.basis)}
              </span>
            </>
          )}
        </div>

        {/* V23a. WHAT THE CLIENT CAN SEE, WHICH IS A DIFFERENT QUESTION FROM WHAT
            THIS BOARD SHOWS. 18-... §3: the client portal reads published state and
            never a scheduler's working state, so a board with no publication line
            is a board on which somebody can move six people and believe the client
            watched them do it. Icon and word, never colour alone. */}
        <div className="board-headline">
          {(() => {
            const strip = publicationStrip(payload.publication, t);
            return (
              <>
                <StatusChip tone={strip.tone} label={strip.label} />
                <span className="muted">{strip.sentence}</span>
                {strip.publishable ? (
                  <button type="button" className="btn" disabled={busy} onClick={() => void publish()}>
                    {t("Publish roster")}
                  </button>
                ) : null}
              </>
            );
          })()}
        </div>

        {payload.readiness === null ? null : <ReadinessExplained readiness={payload.readiness} />}

        {notice ? (
          <div className="form-message" role="status" aria-live="polite">
            <StatusChip tone="attention" label={t("Note")} />
            <span>{notice}</span>
          </div>
        ) : null}

        {payload.vocabulary.states.length === 0 ? (
          <p className="muted">
            {t("The list of what may happen next did not load, so this board can show who is booked but cannot offer any change. Reload to try again.")}
          </p>
        ) : null}

        <div className="board-toolbar">
          <div className="row" role="group" aria-label={t("View")}>
            <button
              type="button"
              className={view === "grid" ? "btn" : "btn btn-quiet"}
              aria-pressed={view === "grid"}
              onClick={() => setView("grid")}
            >
              {t("Grid")}
            </button>
            <button
              type="button"
              className={view === "timeline" ? "btn" : "btn btn-quiet"}
              aria-pressed={view === "timeline"}
              onClick={() => setView("timeline")}
            >
              {t("Timeline")}
            </button>
            {/* MAP IS IN THE APPROVED COMP AND IS NOT BUILT. Disabled with the
                reason attached rather than absent: a toggle drawn in a comp and
                missing from the product is a question every reviewer asks
                twice. There is no venue geometry in the data model yet. */}
            <button type="button" className="btn btn-quiet" disabled title={t("A venue map needs the venue's floor plan, which Verbunk does not hold yet.")}>
              {t("Map")}
            </button>
          </div>

          <label className="board-filter">
            <span className="muted">{t("Filter")}</span>
            <input
              ref={filterBox}
              type="search"
              value={filter.text}
              placeholder={t("Post, role or person")}
              onChange={(e) => setFilter((prior) => ({ ...prior, text: e.target.value }))}
            />
          </label>

          <label>
            <span className="muted">{t("State")}</span>
            <select
              value={filter.state ?? ""}
              onChange={(e) => setFilter((prior) => ({ ...prior, state: e.target.value === "" ? null : e.target.value }))}
            >
              <option value="">{t("Any state")}</option>
              {payload.vocabulary.states.map((s) => (
                <option key={s.id} value={s.id}>
                  {translateLabel(locale, s.label)}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="muted">{t("Role")}</span>
            <select
              value={filter.role ?? ""}
              onChange={(e) => setFilter((prior) => ({ ...prior, role: e.target.value === "" ? null : e.target.value }))}
            >
              <option value="">{t("Any role")}</option>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {translateLabel(locale, r)}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="muted">{t("Post")}</span>
            <select
              value={filter.zone ?? ""}
              onChange={(e) => setFilter((prior) => ({ ...prior, zone: e.target.value === "" ? null : e.target.value }))}
            >
              <option value="">{t("Any post")}</option>
              {zones.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="muted">{t("Group by")}</span>
            <select value={grouping} onChange={(e) => setGrouping(e.target.value as BoardGrouping)}>
              <option value="none">{t("Nothing")}</option>
              <option value="zone">{t("Post")}</option>
              <option value="role">{t("Role")}</option>
            </select>
          </label>
        </div>

        <div className="board-toolbar">
          <span className="muted">
            {isFiltered(filter)
              ? t("{shown} of {total} posts (filtered) · {selected} selected", { shown: shown.length, total: rows.length, selected: selected.length })
              : t("{shown} of {total} posts · {selected} selected", { shown: shown.length, total: rows.length, selected: selected.length })}
          </span>
          {canManage && selected.length > 0 && payload.vocabulary.transitions.length > 0 ? (
            <BulkMoves
              rows={rows}
              selected={selected}
              vocabulary={payload.vocabulary}
              busy={busy}
              onApply={(toState, toLabel) => void moveSelection(toState, toLabel)}
            />
          ) : null}
          {undoable.length > 0 ? (
            <button
              type="button"
              className="btn btn-quiet"
              disabled={busy || undoBlocked !== null}
              title={undoBlocked ?? t("Records the reversal as its own change. Nothing is deleted.")}
              onClick={() => void undo()}
            >
              {/* THE CONTROL RENDERS ITS STORED STATE. A button that said
                  only "Undo" would be a control whose meaning the user has to
                  remember; this one names the change it would reverse. */}
              {undoable.length === 1
                ? t("Undo: {name} back to {state}", { name: undoable[0]!.workerName, state: undoable[0]!.fromLabel })
                : t("Undo: {count} changes", { count: undoable.length })}
            </button>
          ) : null}
        </div>

        {undoBlocked ? (
          <p className="muted" role="status">
            {undoBlocked}
          </p>
        ) : null}

        {rows.length === 0 ? (
          <p className="muted">
            {t("Nothing is planned on this event yet. Add a requirement — one role, one shift, one headcount — and the board fills in around it.")}
          </p>
        ) : null}

        {rows.length > 0 && shown.length === 0 ? (
          <p className="muted">{t("No post matches that filter. Clear it with Escape.")}</p>
        ) : null}
      </section>

      {shown.length > 0 ? (
        <section className="panel">
          <div
            className="board-scroll"
            ref={scroller}
            onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            // A LIST, NOT A TABLE, and both renderers say so — the timeline is
            // not a table at all and a role that changed with the view would
            // make the two views different screens to a screen reader.
            role="list"
            aria-label={view === "grid" ? t("Posts, as a grid") : t("Posts, on a timeline")}
            tabIndex={0}
          >
            {/* THE VIRTUAL SPACERS. Their height is data — 30,142 pixels of
                rows nobody is looking at — and no stylesheet can hold it. The
                variable is all that crosses; design/board.css decides it is a
                height. See check D of verify-design-parity.sh. */}
            <div className="board-pad" style={{ "--board-pad": `${window_.padTop}px` } as React.CSSProperties} aria-hidden="true" />
            {lines.slice(window_.start, window_.end).map((line, i) =>
              line.kind === "heading" ? (
                <div className="board-heading" key={`h-${window_.start + i}`} role="listitem">
                  {line.label}
                </div>
              ) : view === "grid" ? (
                <GridLine
                  key={line.row.requirementId ?? "detached"}
                  row={line.row}
                  focused={focus.row === line.index ? focus.cell : null}
                  selected={selected}
                  onFocusCell={(cell) => setFocus({ row: line.index, cell })}
                  onOpen={(assignmentId) => {
                    setOpenCell(openCell === assignmentId ? null : assignmentId);
                    void showHistory(assignmentId);
                  }}
                  onSelect={(assignmentId) => setSelected((prior) => toggleSelection(prior, assignmentId))}
                  onMore={() => setOpenPost(line.row.requirementId ?? "detached")}
                />
              ) : (
                <TimelineLine
                  key={line.row.requirementId ?? "detached"}
                  row={line.row}
                  extent={extent}
                  focused={focus.row === line.index ? focus.cell : null}
                  selected={selected}
                  onFocusCell={(cell) => setFocus({ row: line.index, cell })}
                  onOpen={(assignmentId) => {
                    setOpenCell(openCell === assignmentId ? null : assignmentId);
                    void showHistory(assignmentId);
                  }}
                />
              ),
            )}
            <div className="board-pad" style={{ "--board-pad": `${window_.padBottom}px` } as React.CSSProperties} aria-hidden="true" />
          </div>
        </section>
      ) : null}

      {(() => {
        const post = openPost === null ? undefined : rows.find((r) => (r.requirementId ?? "detached") === openPost);
        return post ? (
          <PostPanel
            row={post}
            onClose={() => setOpenPost(null)}
            onOpen={(assignmentId) => {
              setOpenCell(assignmentId);
              void showHistory(assignmentId);
            }}
          />
        ) : null;
      })()}

      {openCell !== null ? (
        <CellPanel
          assignmentId={openCell}
          rows={rows}
          vocabulary={payload.vocabulary}
          history={history[openCell] ?? []}
          canManage={canManage}
          busy={busy}
          reason={reason}
          onReason={setReason}
          onClose={() => setOpenCell(null)}
          onMove={(assignment, toState, toLabel, isReserve) => void move(assignment, toState, toLabel, isReserve)}
        />
      ) : null}

      <section className="panel">
        <details>
          <summary>{t("Keyboard")}</summary>
          {/* RENDERED FROM THE SAME TABLE THE HANDLER READS. A legend written
              separately in JSX is a legend that can disagree with the product,
              and it would be believed. */}
          <ul className="board-keys">
            {BOARD_KEYS.map(({ label, ...key }) => ({ ...key, label: translateLabel(locale, label) })).map((k) => (
              <li key={k.key}>
                <kbd>{k.label}</kbd> <span className="muted">{translateLabel(locale, k.action)}</span>
              </li>
            ))}
          </ul>
        </details>
      </section>
    </div>
    </>
  );
}

/** The database's own code for a compensating reversal. Not a word invented here. */
const UNDO_REASON = "undo";

// ---------------------------------------------------------------------------
// GRID — rows as table rows.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// V17 — WHY THE SCORE IS WHAT IT IS.
//
// §V17.5: "`explanation` is not optional. Any number shown to a client must be
// defensible line by line" — and under 06-compliance.md §1 a figure that affects
// worker assignment has to be explainable anyway. Absolute rule 6 says the same
// thing in one sentence: never make an automated decision unexplainable.
//
// THREE THINGS THIS COMPONENT DOES NOT DO.
//   1. It does not compute the score, or any part of it. Every number it prints
//      is a field on the payload, and the identity it prints — 100 x SUM
//      contribution / weight_total — is printed as ARITHMETIC THE READER CAN
//      CHECK, not as arithmetic this file performs.
//   2. It carries no colour-only meaning. Every row states its weight and its
//      readiness in words and digits; 11-... §6 measured the four semantic
//      colours at 1.02:1 to 1.72:1 of each other, which in greyscale is one
//      colour.
//   3. It sets no CSS property inline. verify-design-parity check D forbids the
//      CAPABILITY rather than the syntax, so every dimension below is a class in
//      design/board.css.
//
// THE SUMMARY CARRIES THE VALUE, NOT THE NAME. A collapsed row reading "Why this
// number" tells a scheduler nothing; one reading "3 things are holding this back,
// worth 24 points" is the reason they would open it.
// ---------------------------------------------------------------------------
function ReadinessExplained({ readiness }: { readiness: EventReadiness }) {
  const { locale, t, refusal } = useT();
  const blockers = readiness.blockers;
  // DE-DUPLICATED BY POST, and see system/readiness.ts for why: summing over
  // blockers reported 42.2 of 24 weight at risk on the gallery's own fixture.
  const lost = weightAtRisk(blockers);
  const posts = postsAtRisk(blockers);
  const dark = readiness.critical_posts.dark;
  const annotated = readiness.trend.filter((tr) => tr.annotation !== null);

  // NOTHING TO EXPLAIN IS ITS OWN SENTENCE. An empty flyout under a full score
  // reads as a broken panel; saying so reads as an answer.
  if (readiness.score === null) {
    return (
      <p className="muted board-why-none">
        {t("No post on this event counts toward readiness, so there is no score to explain. A score of zero would say something different and would be wrong.")}
      </p>
    );
  }

  return (
    <details className="board-why">
      <summary>
        {blockers.length === 0
          ? t("Nothing is holding this back — see the arithmetic")
          : t("{things} holding this back across {posts}, costing {lost} of {total} weight", {
              things: blockers.length === 1 ? t("1 thing is") : t("{count} things are", { count: blockers.length }),
              posts: posts === 1 ? t("1 post") : t("{count} posts", { count: posts }),
              lost,
              total: readiness.weight_total,
            })}
      </summary>

      {dark.length > 0 ? (
        <div className="board-why-dark">
          <StatusChip tone="failed" label={t("Cannot open")} />
          <span>
            {(() => {
              const posts = dark
                .map((d) => t("{place} — {level}, {n} asked for", { place: d.place, level: translateLabel(locale, d.criticality_label), n: d.headcount }))
                .join("; ");
              return dark.length === 1
                ? t("A post that must be staffed for the event to open has nobody on it at all: {posts}.", { posts })
                : t("{n} posts that must be staffed for the event to open have nobody on them at all: {posts}.", { n: dark.length, posts });
            })()}
          </span>
        </div>
      ) : null}

      {blockers.length > 0 ? (
        <>
          {/* RANKED BY WHAT FIXING IT WOULD MOVE, never by a severity word —
              §V17.3. The server ranks; this list preserves the order it was
              given and does not re-sort, because a second sort is a second
              opinion about which problem matters most. */}
          <ol className="board-why-blockers">
            {blockers.map((b, i) => (
              <li key={`${b.requirement_id}/${b.code}/${i}`}>
                {/* THE UNIT IS SPOKEN EVEN THOUGH IT IS NOT DRAWN. Sighted
                    readers get a right-aligned numeric column; a screen reader
                    was getting "13.13 bookings at North Stand…", because the
                    impact figure abutted a sentence that itself begins with a
                    number. Found by reading the rendered text rather than the
                    markup. */}
                <span className="board-why-impact">
                  <span className="visually-hidden">{t("Costs")} </span>
                  {b.score_impact}
                  <span className="visually-hidden"> weight.</span>
                </span>
                <span className="board-why-said">
                  {translateRefusal(locale, { message: b.sentence, sentence_key: b.sentence_key, params: b.params })}{" "}
                  <em>{translateRefusal(locale, { message: b.action, sentence_key: b.action_key, params: b.action_params })}</em>
                </span>
              </li>
            ))}
          </ol>
        </>
      ) : null}

      <table className="board-why-table">
        <caption>
          {t("Every post that counts, and what it contributes. The score is 100 times the total contribution divided by the total weight — {score} = 100 x {sum} / {weight}.", {
            score: readiness.score ?? "—",
            sum: Math.round(readiness.explanation.reduce((sum, e) => sum + e.contribution, 0) * 10000) / 10000,
            weight: readiness.weight_total,
          })}
        </caption>
        <thead>
          <tr>
            <th scope="col">{t("Post")}</th>
            <th scope="col">{t("Weight")}</th>
            <th scope="col">{t("Ready")}</th>
            <th scope="col">{t("Contributes")}</th>
          </tr>
        </thead>
        <tbody>
          {readiness.explanation.map((line) => (
            <tr key={line.requirement_id}>
              <th scope="row">{line.requirement_id.slice(-8)}</th>
              <td>{line.weight}</td>
              <td>{Math.round(line.readiness * 100)}%</td>
              <td>{Math.round(line.contribution * 10000) / 10000}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {annotated.length > 0 ? (
        <div className="board-why-scope">
          <StatusChip tone="attention" label={t("Scope changed")} />
          <ul>
            {annotated.map((tr) => (
              <li key={tr.at}>{tr.annotation}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </details>
  );
}

type LineProps = {
  row: BoardRow;
  focused: number | null;
  selected: string[];
  onFocusCell: (cell: number) => void;
  onOpen: (assignmentId: string) => void;
};

function GridLine({
  row,
  focused,
  selected,
  onFocusCell,
  onOpen,
  onSelect,
  onMore,
}: LineProps & { onSelect: (id: string) => void; onMore: () => void }) {
  const { locale, t, refusal } = useT();
  const cells = cellsFor(row);
  // DUX-16 (owner, 21 October 2026): a row has one fixed height (the virtual window's arithmetic), so the
  // places that do not fit were hidden with no sign. They are counted, and a "+N" opens the post's panel.
  const cellsRef = useRef<HTMLDivElement>(null);
  const hidden = useHiddenCount(cellsRef, cells.length);
  return (
    <div className="board-line" role="listitem">
      <div className="board-post">
        <span className="board-post-name">{row.post}</span>{" "}
        <span className="muted">
          {translateLabel(locale, row.role)} · {translateLabel(locale, row.criticalityLabel)}
        </span>
      </div>
      <div className="board-fill muted">{fillSentence(row, t)}</div>
      <div className="board-cells" ref={cellsRef}>
        {cells.map((cell, i) => (
          <Cell
            key={cell.key}
            row={row}
            cell={cell}
            focused={focused === i}
            selected={cell.kind === "assignment" && selected.includes(cell.assignment.id)}
            onFocus={() => onFocusCell(i)}
            onOpen={onOpen}
            onSelect={onSelect}
          />
        ))}
      </div>
      {hidden > 0 ? (
        <button
          type="button"
          className="btn btn-quiet board-more"
          aria-label={t("{n} more places on this post", { n: hidden })}
          title={t("{n} more places on this post", { n: hidden })}
          onClick={onMore}
        >
          +{hidden}
        </button>
      ) : null}
    </div>
  );
}

/** How many of a row's places sit past its right edge, kept current as the row or the window resizes. */
function useHiddenCount(ref: React.RefObject<HTMLDivElement>, count: number): number {
  const [hidden, setHidden] = useState(0);
  useEffect(() => {
    const box = ref.current;
    if (!box) return;
    const measure = () => {
      const edge = box.getBoundingClientRect().right;
      let n = 0;
      for (const child of Array.from(box.children)) if (child.getBoundingClientRect().right > edge + 1) n += 1;
      setHidden(n);
    };
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(box);
    return () => watch.disconnect();
  }, [ref, count]);
  return hidden;
}

/** DUX-16. Every place on one post, wrapped, so the ones a row could not show can be opened. */
function PostPanel({
  row,
  onOpen,
  onClose,
}: {
  row: BoardRow;
  onOpen: (assignmentId: string) => void;
  onClose: () => void;
}) {
  const { t } = useT();
  const cells = cellsFor(row);
  return (
    <section className="panel">
      <div className="panel-head">
        <h3>{row.post}</h3>
        <button type="button" className="btn btn-quiet" onClick={onClose}>
          {t("Close")}
        </button>
      </div>
      <div className="board-post-places">
        {cells.map((cell) => (
          <Cell key={cell.key} row={row} cell={cell} focused={false} selected={false} onFocus={() => {}} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// TIMELINE — THE SAME ROWS, POSITIONED.
//
// §V14.3: "Do not virtualise the time axis in the timeline view — horizontal
// extent is bounded by the event, and virtualising it breaks the at-a-glance
// overlap reading that is the view's entire purpose." So the bar spans the
// whole event and every cell in it renders; only the rows window.
// ---------------------------------------------------------------------------

function TimelineLine({
  row,
  extent,
  focused,
  selected,
  onFocusCell,
  onOpen,
}: LineProps & { extent: { from: number; to: number } | null }) {
  const { locale, t, refusal } = useT();
  const { me } = useSession();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const cells = cellsFor(row);
  const geometry = extent === null ? { leftPct: 0, widthPct: 100 } : barGeometry(row, extent);
  return (
    <div className="board-line" role="listitem">
      <div className="board-post">
        <span className="board-post-name">{row.post}</span>{" "}
        <span className="muted">
          {translateLabel(locale, row.role)} · {translateLabel(locale, row.criticalityLabel)}
        </span>
      </div>
      <div className="board-lane">
        <div
          className="board-bar"
          style={{ "--board-bar-left": `${geometry.leftPct}%`, "--board-bar-width": `${geometry.widthPct}%` } as React.CSSProperties}
          // THE TIMES ARE IN WORDS ON THE BAR ITSELF. A bar whose only content
          // is its position is a bar that says nothing to anybody who cannot
          // see it, and its position is the one thing a screen reader cannot
          // read out.
          title={`${whenIn(row.startsAt, orgZone)} → ${clock(row.endsAt)} · ${span(row.startsAt, row.endsAt)}`}
        >
          <span className="board-bar-label">{fillSentence(row, t)}</span>
          <div className="board-cells">
            {cells.map((cell, i) => (
              <Cell
                key={cell.key}
                row={row}
                cell={cell}
                focused={focused === i}
                selected={cell.kind === "assignment" && selected.includes(cell.assignment.id)}
                onFocus={() => onFocusCell(i)}
                onOpen={onOpen}
                compact
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ONE CELL, SHARED BY BOTH RENDERERS.
//
// §V14.7 check 2 asks that both renderers consume the identical view model.
// They consume the identical CELL as well — the same component, the same
// accessible name, the same tone — which is the strongest available form of
// that: there is no second rendering to diverge.
// ---------------------------------------------------------------------------

function Cell({
  row,
  cell,
  focused,
  selected,
  compact,
  onFocus,
  onOpen,
  onSelect,
}: {
  row: BoardRow;
  cell: BoardCell;
  focused: boolean;
  selected: boolean;
  compact?: boolean;
  onFocus: () => void;
  onOpen: (assignmentId: string) => void;
  onSelect?: (assignmentId: string) => void;
}) {
  const { locale, t, refusal } = useT();
  const name = cellName(row, cell, undefined, t);
  if (cell.kind === "unknown") {
    // ★ THE SENTENCE IS IN THE DOM, NOT IN AN aria-label, AND THAT IS THE FIX.
    //
    // This carried `aria-label` on a bare <span>. The parent is a plain
    // <div className="board-cells"> with no grid role, so this element is
    // role=generic — and ARIA 1.2 PROHIBITS naming a generic role. A screen
    // reader is free to ignore the label entirely, and what is then guaranteed
    // to be announced is the visible text: "?".
    //
    // §5 of 12-assignment-spine-and-planning-board.md requires every status cell
    // to expose its state and cause in its accessible name, and this cell is
    // emitted precisely when the readiness call FAILED — so the one cell whose
    // cause a reader most needs was the one that could not deliver it.
    //
    // Visually-hidden text needs no ARIA, is announced everywhere, and is the
    // pattern the vacancy branch four lines below already argues for: "A WORD,
    // NOT A DASH. A dash is a shape a screen reader announces as nothing." The
    // `?` is now aria-hidden so it is not read as punctuation beside the words.
    return (
      <span className="board-cell board-cell-unknown" title={name}>
        <span aria-hidden="true">?</span>
        <span className="visually-hidden">{name}</span>
      </span>
    );
  }
  if (cell.kind === "vacancy") {
    return (
      <button
        type="button"
        className={`board-cell board-cell-vacancy${focused ? " is-focused" : ""}`}
        aria-label={name}
        title={name}
        onFocus={onFocus}
        onClick={onFocus}
      >
        {/* A WORD, NOT A DASH. A dash is a shape a screen reader announces as
            nothing and a colour-blind reader reads as an artefact. */}
        {compact ? t("Open") : t("Open place")}
      </button>
    );
  }
  const assignment = cell.assignment;
  return (
    <span className={`board-cell${focused ? " is-focused" : ""}${selected ? " is-selected" : ""}`}>
      <button
        type="button"
        className="board-cell-open"
        aria-label={name}
        title={name}
        onFocus={onFocus}
        onClick={() => {
          onFocus();
          onOpen(assignment.id);
        }}
      >
        <StatusChip tone={toneFor({ status_fills_headcount: assignment.fillsHeadcount, status_is_terminal: assignment.isTerminal })} label={translateLabel(locale, assignment.stateLabel)} />
        <span className="board-cell-name">{assignment.workerName}</span>
        {assignment.isReserve ? <StatusChip tone="neutral" label={t("Standby")} /> : null}
      </button>
      {onSelect ? (
        <label className="board-cell-select">
          <input type="checkbox" checked={selected} onChange={() => onSelect(assignment.id)} />
          <span className="visually-hidden">{t("Select {name}", { name: assignment.workerName })}</span>
        </label>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// WHAT A SELECTION MAY BE MOVED TO.
//
// THE INTERSECTION OF THE EDGES EVERY SELECTED PERSON HAS, and the intersection
// rather than the union is the whole decision. A union would offer a move that
// applies to nine of forty and refuse thirty-one — thirty-one refusals a
// scheduler then has to read individually, from a control that implied they
// would all go through.
// ---------------------------------------------------------------------------

function BulkMoves({
  rows,
  selected,
  vocabulary,
  busy,
  onApply,
}: {
  rows: BoardRow[];
  selected: string[];
  vocabulary: AssignmentStateVocabulary;
  busy: boolean;
  onApply: (toState: string, toLabel: string) => void;
}) {
  const { locale, t, refusal } = useT();
  const states = new Set(
    rows
      .flatMap((r) => r.assignments)
      .filter((a) => selected.includes(a.id))
      .map((a) => a.state),
  );
  const common = vocabulary.transitions.filter(
    (edge) => edge.from_state !== null && [...states].every((s) => vocabulary.transitions.some((e) => e.from_state === s && e.to_state === edge.to_state)),
  );
  const unique: typeof common = [];
  for (const edge of common) if (!unique.some((u) => u.to_state === edge.to_state)) unique.push(edge);

  if (unique.length === 0) {
    return (
      <span className="muted">
        {t("There is no single move every selected booking can make. Narrow the selection, or move them one at a time.")}
      </span>
    );
  }
  return (
    <div className="row">
      {unique.map((edge) => (
        <button
          key={edge.to_state}
          type="button"
          className="btn btn-quiet"
          disabled={busy}
          title={edge.description}
          onClick={() => onApply(edge.to_state, edge.to_label)}
        >
          {translateLabel(locale, edge.to_label)} · {selected.length}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// THE CELL PANEL — everything V13's list did, for one booking.
// ---------------------------------------------------------------------------

function CellPanel({
  assignmentId,
  rows,
  vocabulary,
  history,
  canManage,
  busy,
  reason,
  onReason,
  onClose,
  onMove,
}: {
  assignmentId: string;
  rows: BoardRow[];
  vocabulary: AssignmentStateVocabulary;
  history: AssignmentTransition[];
  canManage: boolean;
  busy: boolean;
  reason: string;
  onReason: (value: string) => void;
  onClose: () => void;
  onMove: (
    assignment: { id: string; state: string; stateLabel: string; isReserve: boolean; workerName: string },
    toState: string | null,
    toLabel: string,
    isReserve?: boolean,
  ) => void;
}) {
  const { locale, t, refusal } = useT();
  const { me } = useSession();
  const orgZone = me !== null && me.authenticated ? (me.active_org?.timezone ?? null) : null;
  const found = rows.flatMap((r) => r.assignments).find((a) => a.id === assignmentId);
  if (!found) return null;
  const subject = {
    id: found.id,
    state: found.state,
    stateLabel: found.stateLabel,
    isReserve: found.isReserve,
    workerName: found.workerName,
  };
  return (
    <section className="panel">
      <div className="panel-head">
        <h3>
          {found.workerName} · {translateLabel(locale, found.roleName)}
        </h3>
        <button type="button" className="btn btn-quiet" onClick={onClose}>
          {t("Close")}
        </button>
      </div>
      <p>
        <span
          aria-label={accessibleName({
            worker_name: found.workerName,
            role_name: found.roleName,
            status_label: found.stateLabel,
            status_fills_headcount: found.fillsHeadcount,
            status_is_terminal: found.isTerminal,
            is_reserve: found.isReserve,
          }, t)}
        >
          <StatusChip tone={toneFor({ status_fills_headcount: found.fillsHeadcount, status_is_terminal: found.isTerminal })} label={translateLabel(locale, found.stateLabel)} />
        </span>{" "}
        <span className="muted">
          {whenIn(found.startsAt, orgZone)} → {clock(found.endsAt)} · {span(found.startsAt, found.endsAt)}
        </span>
      </p>

      {canManage && vocabulary.reasons.length > 0 ? (
        <label>
          {t("Reason for the next change")}
          <select value={reason} onChange={(e) => onReason(e.target.value)}>
            {/* BLANK IS A REAL CHOICE and it is the default. A reason that had
                to be given would be a reason somebody invents. */}
            <option value="">{t("Not stated")}</option>
            {vocabulary.reasons.map((r) => (
              <option key={r.id} value={r.id}>
                {translateLabel(locale, r.label)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {canManage ? (
        <div className="row">
          {vocabulary.transitions
            .filter((tr) => tr.from_state === found.state)
            .map((edge) => (
              <button
                key={edge.to_state}
                type="button"
                className="btn btn-quiet"
                disabled={busy}
                title={edge.description}
                onClick={() => onMove(subject, edge.to_state, edge.to_label)}
              >
                {translateLabel(locale, edge.to_label)}
              </button>
            ))}
          {found.isReserve ? (
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => onMove(subject, null, found.stateLabel, false)}
            >
              {t("Promote from standby")}
            </button>
          ) : null}
        </div>
      ) : null}

      <ol className="timeline">
        {history.map((tr) => (
          <li key={tr.seq}>
            <span>
              {tr.from_label === null
                ? t("Booked as {state}", { state: translateLabel(locale, tr.to_label) })
                : tr.from_state === tr.to_state
                  ? tr.to_is_reserve
                    ? t("Moved to standby")
                    : t("Promoted from standby")
                  : `${translateLabel(locale, tr.from_label)} → ${translateLabel(locale, tr.to_label)}`}
            </span>{" "}
            <span className="muted">{whenIn(tr.occurred_at, orgZone)}</span>
            {tr.reason_label ? <span className="muted"> · {translateLabel(locale, tr.reason_label)}</span> : null}
          </li>
        ))}
        {history.length === 0 ? <li className="muted">{t("No history has loaded for this booking.")}</li> : null}
      </ol>
    </section>
  );
}
